import React, { useMemo } from "react";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import OrderApplicantDetail from "../../components/orderApplicantDetail";
import OrderInvestmentClientRpq from "../../components/orderInvestmentClientRpq";
import OrderInvestmentAccount from "../../components/orderInvestmentAccount";
import OrderInvestmentTransactionActionList from "../../components/orderInvestmentTransactionActionList";
import OrderInvestmentTransactionStatusHeader from "../../components/orderInvestmentTransactionStatusHeader";
import OrderArFormPartA from "../../components/orderArFormPartA";
import OrderArFormPartB from "../../components/orderArFormPartB";
import OrderArFormPartC from "../../components/orderArFormPartC";
import OrderArFormPartD from "../../components/orderArFormPartD";
import OrderArFormPartE from "../../components/orderArFormPartE";
import OrderInvestmentTransactionCommission from "../../components/orderInvestmentTransactionCommission";
import FileList from "../../components/fileList";
import ActivityLog from "../../components/activityLog";
import {
  InvestmentTransactionSettingsProvider,
  useInvestmentTransactionSettings,
} from "../../contexts/investmentTransactionSettingsContext";
import {
  DEFAULT_AR_FORM,
  createArFormValidators,
} from "../../helpers/arFormUtils";

const apiEntity = "orderInvestmentTransaction";

const baseEditFormSchema = {
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
  },
  title: "Investment Transaction",
  titleField: "code",
  apiEntity,
  initialValues: {
    arForm: DEFAULT_AR_FORM(),
  },
  layout: {
    containers: [
      {
        key: "main-g",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        props: {
          gutter: "xl",
          columns: 24,
        },
        cols: [
          { key: "main-g1", props: { span: 7 } },
          { key: "main-g2", props: { span: 17 } },
        ],
      },
      {
        key: "section-applicant",
        parent: "main-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Applicant Details",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "section-action-list",
        parent: "main-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Action List",
          withBorder: false,
          paperPadding: "sm",
          style: { minHeight: 0 },
          mt: 0,
          mb: 20,
        },
      },
      {
        key: "main-tab",
        parent: "main-g2",
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default",
          defaultValue: "Transaction Detail",
        },
        tab: [
          { key: "tab-transaction-detail", label: "Transaction Detail" },
          { key: "tab-commission", label: "Commission" },
          { key: "tab-files", label: "Files" },
          { key: "tab-logs", label: "Logs" },
        ],
      },
      {
        key: "section-files",
        parent: "tab-files",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Files",
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: "lg",
        },
      },
      {
        key: "section-log",
        parent: "tab-logs",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Activity Log",
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: "lg",
        },
      },
      {
        key: "section-transaction-status",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          paperPadding: "sm",
          style: { minHeight: 0 },
          mt: 0,
          mb: "sm",
        },
      },
      {
        key: "section-investment-client-rpq",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Client & RPQ",
          description: "Read-only — pulled from the linked client record.",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-investment-account",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Investment Account",
          description: "iFAST account number from the parent order.",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-a",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part A — Transaction Details 交易資料",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-b",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part B — Advisor Recommendation 理財建議",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-c",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part C — Commission 佣金",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-e",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part E — Client Declaration 戊部 - 客戶聲明",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-commission-summary",
        parent: "tab-commission",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Commission",
          description:
            "iFast Master Logsheet — charge breakdown (read-only, derived from Part A & Part C)",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-d",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part F — Advisor Declaration 己部 - 理財顧問聲明",
          minHeight: 0,
          mb: "lg",
        },
      },
    ],
    fields: [
      {
        name: "orderId.clientSnapShot",
        component: OrderApplicantDetail,
        parent: "section-applicant",
        props: {},
      },
      {
        name: "actionList",
        component: OrderInvestmentTransactionActionList,
        parent: "section-action-list",
        props: {},
      },
      {
        name: "transactionStatus",
        component: OrderInvestmentTransactionStatusHeader,
        parent: "section-transaction-status",
        props: {},
      },
      {
        name: "investmentClientRpq",
        component: OrderInvestmentClientRpq,
        parent: "section-investment-client-rpq",
        props: { orderSourceFromField: "orderId" },
      },
      {
        name: "investmentAccount",
        component: OrderInvestmentAccount,
        parent: "section-investment-account",
        props: { orderSourceFromField: "orderId", readOnly: true },
      },
      {
        name: "arFormPartA",
        component: OrderArFormPartA,
        parent: "section-ar-form-part-a",
        props: {},
      },
      {
        name: "arFormPartB",
        component: OrderArFormPartB,
        parent: "section-ar-form-part-b",
        props: {},
      },
      {
        name: "arFormPartC",
        component: OrderArFormPartC,
        parent: "section-ar-form-part-c",
        props: {},
      },
      {
        name: "arFormPartE",
        component: OrderArFormPartE,
        parent: "section-ar-form-part-e",
        props: {},
      },
      {
        name: "commissionSummary",
        component: OrderInvestmentTransactionCommission,
        parent: "section-commission-summary",
        props: {},
      },
      {
        name: "arFormPartD",
        component: OrderArFormPartD,
        parent: "section-ar-form-part-d",
        props: {},
      },
      {
        name: "files",
        component: FileList,
        parent: "section-files",
        props: {},
      },
      {
        name: "log",
        component: ActivityLog,
        parent: "section-log",
        props: {},
      },
    ],
  },
};

const withOrderSource = (Component) =>
  function OrderSourceField({ form, orderSourceFromField, ...props }) {
    const orderSource = orderSourceFromField
      ? form?.values?.[orderSourceFromField]
      : null;
    return <Component form={form} orderSource={orderSource} {...props} />;
  };

baseEditFormSchema.layout.fields = baseEditFormSchema.layout.fields.map((field) => {
  if (field.name === "investmentClientRpq") {
    return { ...field, component: withOrderSource(OrderInvestmentClientRpq) };
  }
  if (field.name === "investmentAccount") {
    return { ...field, component: withOrderSource(OrderInvestmentAccount) };
  }
  return field;
});

export const getOrderInvestmentTransactionEditFormSchema = () => ({
  ...baseEditFormSchema,
});

function OrderInvestmentTransactionInner({ role = "OPERATION" }) {
  const {
    nonWrapUpfrontFeeRules,
    nonWrapMaxSubscriptionFee,
    onlineIfastCharge,
    paperIfastCharge,
  } = useInvestmentTransactionSettings();

  const formSchema = useMemo(
    () => ({
      ...baseEditFormSchema,
      validate: createArFormValidators(nonWrapUpfrontFeeRules, {
        nonWrapMaxSubscriptionFee,
        onlineIfastCharge,
        paperIfastCharge,
      }),
    }),
    [
      nonWrapUpfrontFeeRules,
      nonWrapMaxSubscriptionFee,
      onlineIfastCharge,
      paperIfastCharge,
    ]
  );

  const tableSchema = {
    showActionColumn: false,
    columns: [{ field: "code", headerName: "Transaction No." }],
  };

  return (
    <PageList
      title="Investment Transaction"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
      accessLinkEntity="order"
      initSort={{ by: "createdAt", order: "desc" }}
    />
  );
}

export default function OrderInvestmentTransaction({ role = "OPERATION" }) {
  return (
    <InvestmentTransactionSettingsProvider>
      <OrderInvestmentTransactionInner role={role} />
    </InvestmentTransactionSettingsProvider>
  );
}
